import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { useT } from "@/i18n/useT";
import { toast } from "sonner";

export default function ProductPage() {
  const t = useT();
  const p = t.product;
  const { handle } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    api.get(`/products/${handle}`).then(({ data }) => { setProduct(data.product); setRelated(data.related || []); setVariant(data.product?.variants?.[0] || null); }).catch(() => setProduct(null));
  }, [handle]);

  if (!product) return <div data-testid="product-loading" className="br-container py-40 text-center text-walnut italic">{p.loading}</div>;

  const handleAdd = () => { if (!variant) return; addItem(product, variant, 1); toast(p.added(product.title)); };

  return (
    <div data-testid="product-page" className="br-container py-12 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7 space-y-4">
          {product.images.map((src, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden bg-walnut/5 br-fade-in">
              <img src={src} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="md:col-span-5">
          <div className="md:sticky md:top-32">
            <div className="br-eyebrow mb-4">{p.category[product.category] || product.category}</div>
            <h1 className="font-serif text-3xl md:text-5xl text-espresso leading-tight">{product.title}</h1>
            <div className="mt-6 text-2xl font-serif text-walnut">€{parseFloat(product.price).toFixed(2)}</div>
            {product.variants.length > 1 && (
              <div className="mt-10">
                <div className="text-[11px] uppercase tracking-[0.3em] text-walnut mb-3">{p.size}</div>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((v) => (
                    <button key={v.id} data-testid={`variant-${v.id}`} onClick={() => setVariant(v)}
                      className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${variant?.id === v.id ? "border-espresso bg-espresso text-cream" : "border-walnut/40 text-espresso hover:border-espresso"}`}>
                      {v.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="add-to-bag-button" onClick={handleAdd} className="mt-10 w-full bg-espresso text-cream py-4 text-xs uppercase tracking-[0.3em] hover:bg-dark-walnut transition-colors duration-500">{p.addToBag}</button>
            <div className="mt-10 text-base font-light text-dark-walnut leading-relaxed whitespace-pre-line">{product.description}</div>
            {product.why_baltic && (
              <div className="mt-12 pt-10 border-t border-walnut/20">
                <div className="br-eyebrow mb-4">{p.whyBaltic}</div>
                <p className="text-sm font-light text-dark-walnut leading-relaxed italic">{product.why_baltic}</p>
              </div>
            )}
            <div className="mt-12 pt-10 border-t border-walnut/20 grid grid-cols-2 gap-6 text-xs text-walnut">
              <div><div className="uppercase tracking-[0.25em] mb-2">{p.madeIn}</div><div className="text-espresso text-sm font-light">{p.madeInValue}</div></div>
              <div><div className="uppercase tracking-[0.25em] mb-2">{p.shipping}</div><div className="text-espresso text-sm font-light">{p.shippingValue}</div></div>
            </div>
          </div>
        </div>
      </div>
      {related.length > 0 && (
        <section className="mt-32 pt-20 border-t border-walnut/20" data-testid="related-section">
          <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-12">{p.related}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {related.map((prod) => <ProductCard key={prod.id} product={prod} />)}
          </div>
        </section>
      )}
      <div className="mt-20 text-center"><Link to="/shop" className="br-link-underline text-xs uppercase tracking-[0.3em]">{p.backToShop}</Link></div>
    </div>
  );
}